// AICampaignRecommender.jsx — Interactive AI Recommender for marketing campaigns.
//
// What it does:
//   • Takes the deduped customer list from the parent (CustomersSmart / MarketingManager).
//   • Calls POST /api/ai/campaign-recommender which:
//       1) classifies each customer into a Saudi-Pro RFM segment server-side,
//       2) reads past winning campaigns to extract a template,
//       3) computes expected recovery count + revenue + RLR per segment,
//       4) (optionally) polishes the title/body with Claude via Emergent LLM.
//   • Shows ranked recommendations with priority + an "نفّذ الآن" CTA that opens
//     the WhatsApp blast modal pre-populated with the suggested template.

import React, { useEffect, useState } from "react";
import { api, formatErr } from "./lib/api";
import { isZeroAudience } from "./lib/campaignAudienceGate";
import { InfoTip } from "./components/InfoTip";

const fm = (n, d = 0) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: d });

const PRIORITY_META = {
  P0: { label: "🔴 عاجل اليوم",  color: "#B91C1C", bg: "rgba(185,28,28,0.10)" },
  P1: { label: "🟠 هذا الأسبوع", color: "#D97706", bg: "rgba(217,119,6,0.08)" },
  P2: { label: "🟢 دوري",       color: "#0F766E", bg: "rgba(15,118,110,0.08)" },
};

export default function AICampaignRecommender({ onLaunch }) {
  // v200.39 (بند ١) — لا نمرّر customers من datasets.cust. المستشار
  // يقرأ من pos_sales عبر /api/customers/segment-stats داخل الخادم.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [polish, setPolish] = useState(true);

  const fetchRecs = async () => {
    setLoading(true); setErr(null);
    try {
      const payload = { polish_with_llm: polish };
      const { data } = await api.post("/ai/campaign-recommender", payload, { timeoutClass: "AI_CALL" });
      setData(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || "تعذّر توليد التوصيات");
    } finally { setLoading(false); }
  };

  // v200.39 — auto-load on first mount (لا حاجة لعملاء من المتصفح)
  useEffect(() => {
    if (!data && !loading) fetchRecs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card" data-testid="ai-campaign-recommender" style={{ padding: 0, overflow: "hidden", marginBottom: 14 }}>
      {/* Hero header */}
      <div style={{
        padding: "16px 20px",
        background: "linear-gradient(135deg, #1E3A8A 0%, #7C3AED 60%, #EC4899 130%)",
        color: "#fff",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 700 }}>🤖 المستشار الذكي للحملات</div>
            <h3 style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 800 }}>
              ماذا تطلق هذا الأسبوع؟
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, opacity: 0.9 }}>
              يحلّل قاعدة عملائك + بيانات الحملات السابقة، ويقترح خطوات تنفيذية بأرقام متوقعة.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ fontSize: 11, color: "#fff", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="checkbox" checked={polish} onChange={(e) => setPolish(e.target.checked)}
                data-testid="rec-polish-toggle"
              />
              ✨ صياغة AI
            </label>
            <button
              type="button"
              onClick={fetchRecs}
              disabled={loading}
              data-testid="rec-refresh"
              style={{
                padding: "8px 14px", borderRadius: 8, border: "none",
                background: "#fff", color: "#7C3AED", fontWeight: 800, cursor: "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "⏳ يحلّل..." : "🔄 حدّث التوصيات"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {err && (
          <div style={{ background: "rgba(220,38,38,0.10)", color: "#B91C1C", padding: 10, borderRadius: 8, fontSize: 13 }}>
            ⚠ {err}
          </div>
        )}

        {!loading && !data && !err && (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
            انقر «حدّث التوصيات» لتلقّي اقتراحات مخصّصة لقاعدة عملائك.
          </div>
        )}

        {loading && !data && (
          <div style={{ padding: 30, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
            ⏳ المستشار يحلّل قاعدة عملائك + الحملات السابقة...
          </div>
        )}

        {data && data.recommendations?.length === 0 && (
          <div style={{ padding: 20, color: "var(--muted)", fontSize: 13 }}>
            {data.message || "لا توجد توصيات حالياً."}
          </div>
        )}

        {data && data.recommendations?.length > 0 && (
          <>
            <div data-testid="ai-rec-envelope" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12, fontSize: 11, color: "var(--muted)", alignItems: "center" }}>
              <span data-testid="ai-rec-analyzed">📊 {fm(data.customers_analyzed)} عميل تم تحليله</span>
              {/* v200.39 (بند ٥) — «غير منسوب» ظاهر لا صفر مقنَّع. */}
              {(data.unattributed_count > 0) && (
                <span data-testid="ai-rec-unattributed" style={{ background: "rgba(217,119,6,0.10)", padding: "2px 8px", borderRadius: 6, color: "#B45309", fontWeight: 700 }}>
                  ⚠ غير منسوب {fm(data.unattributed_count)}
                </span>
              )}
              {/* v200.39 (بند ٤) — تفصيل الاستبعاد على مستوى الاستجابة. */}
              {(() => {
                const br = data.excluded_breakdown || {};
                const total = br.total || 0;
                if (total === 0) {
                  return (
                    <span data-testid="ai-rec-excluded-zero" style={{ fontSize: 11, opacity: 0.85 }}>
                      · مستبعَد 0 (مُثبت — لا صفوف بلا مفتاح في النافذة)
                    </span>
                  );
                }
                return (
                  <span data-testid="ai-rec-excluded-total" style={{ fontSize: 11 }}>
                    · مستبعَد {fm(total)} = بلا رقم {fm(br.no_phone || 0)} + اشتقاق مفتاح فشل {fm(br.key_derivation_failed || 0)}
                  </span>
                );
              })()}
              {data.envelope?.window && (
                <span data-testid="ai-rec-window" style={{ fontSize: 11, opacity: 0.85 }}>· نافذة {data.envelope.window} · {data.envelope.mode}</span>
              )}
              {data.based_on_campaign && (
                <span>🎯 مبني على حملة «<b style={{ color: "var(--brown-deep)" }}>{data.based_on_campaign}</b>» (Uplift {fm(data.based_on_uplift, 1)}%)</span>
              )}
              {data.historical_cic > 0 && <span>💸 CIC تاريخي: {fm(data.historical_cic, 1)} ر</span>}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {data.recommendations.map((rec, idx) => {
                const meta = PRIORITY_META[rec.priority] || PRIORITY_META.P2;
                // v200.42 (بند ٣) — بطاقة جمهورها 0 معطَّلة صراحةً،
                // الأرقام المتوقعة تُصفَّر بصرياً، والزر لا يُنفَّذ.
                // v200.46 — استُخرج المنطق إلى lib/campaignAudienceGate.
                const isEmpty = isZeroAudience(rec.audience_size);
                return (
                  <div key={`${rec.segment_id}-${idx}`} data-testid={`rec-${rec.segment_id}`} style={{
                    border: "1px solid var(--border)",
                    borderInlineStart: `4px solid ${isEmpty ? "#9CA3AF" : meta.color}`,
                    borderRadius: 10,
                    overflow: "hidden",
                    background: "#fff",
                    opacity: isEmpty ? 0.55 : 1,
                  }}>
                    <div style={{
                      padding: "12px 16px",
                      background: meta.bg,
                      display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
                    }}>
                      <div>
                        <span style={{
                          fontSize: 10.5, fontWeight: 800, padding: "3px 9px", borderRadius: 999,
                          background: meta.color, color: "#fff", marginInlineEnd: 8,
                        }}>{meta.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "var(--brown-deep)" }}>
                          {rec.segment_label}
                        </span>
                        {rec.ai_enhanced && (
                          <span style={{ marginInlineStart: 8, fontSize: 10, color: "#7C3AED", fontWeight: 700 }}>
                            ✨ AI
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--brown-deep)", fontWeight: 800 }} data-testid={`rec-audience-${rec.segment_id}`}>
                        👥 الجمهور {fm(rec.audience_size)}
                        <span style={{ color: "#6B7280", fontWeight: 600, marginInlineStart: 6 }}>
                          · مستبعَد {fm(rec.audience_excluded || 0)}
                          {/* v200.42 (بند ٢) — التفصيل السابق كان
                              coverage-scoped لا segment-scoped ولا
                              يجمع أجزاؤه إلى نفسه (119 ≠ 9,136+287).
                              رقم بلا تفصيل أفضل من تفصيل كاذب. */}
                        </span>
                      </div>
                    </div>

                    <div style={{ padding: "12px 16px" }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 6 }}>
                        {rec.title}
                      </div>
                      <div style={{ fontSize: 13, color: "var(--brown-deep)", lineHeight: 1.8, marginBottom: 10 }}>
                        {rec.body}
                      </div>

                      {isEmpty && (
                        <div data-testid={`rec-empty-notice-${rec.segment_id}`} style={{
                          background: "rgba(217,119,6,0.10)", color: "#B45309",
                          padding: "6px 10px", borderRadius: 6, fontSize: 12,
                          fontWeight: 700, marginBottom: 10,
                        }}>
                          ⚠ لا جمهور مؤهَّل — لا حملة تُطلَق (٠ في هذه النافذة).
                        </div>
                      )}

                      {/* Forecast metrics — v200.42 (بند ٣): على جمهور
                          صفر نُظهر «—» بدل رقم متوقع مشتقّ من ثابت. */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 10 }}>
                        <Stat data-testid={`rec-forecast-recovered-${rec.segment_id}`}
                          label="🎯 متوقع استرجاع"
                          value={isEmpty ? "—" : `${fm(rec.expected_recovered)} عميل`}
                          sub={isEmpty ? "جمهور 0" : `بنسبة ${rec.recovery_rate_pct}%`} />
                        <Stat label="💰 إيراد متوقع"
                          value={isEmpty ? "—" : `${fm(rec.expected_revenue)} ر`}
                          color={isEmpty ? "#6B7280" : "#16A34A"} />
                        <Stat label="💸 ميزانية مقترحة"
                          value={isEmpty ? "—" : `${fm(rec.estimated_budget)} ر`}
                          color={isEmpty ? "#6B7280" : "#D97706"} />
                        <Stat label="📈 RLR متوقع"
                          value={isEmpty ? "—" : `${fm(rec.estimated_rlr, 2)}×`}
                          color={isEmpty ? "#6B7280" : (rec.estimated_rlr >= 2 ? "#16A34A" : rec.estimated_rlr >= 1 ? "#D97706" : "#DC2626")} />
                        <Stat label="🎟 خصم مقترح"
                          value={isEmpty ? "—" : `${rec.suggested_discount_pct}%`}
                          color={isEmpty ? "#6B7280" : "#7C3AED"} />
                      </div>

                      {/* WhatsApp template */}
                      <details style={{ marginTop: 6 }}>
                        <summary style={{ cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#0F766E", outline: "none" }}>
                          📱 معاينة نص واتساب المقترح
                        </summary>
                        <div style={{
                          marginTop: 6, padding: 10, background: "#F0FDF4",
                          border: "1px dashed #16A34A", borderRadius: 8,
                          fontSize: 12.5, color: "var(--brown-deep)", lineHeight: 1.8, whiteSpace: "pre-wrap",
                        }}>
                          {rec.whatsapp_template}
                        </div>
                      </details>

                      {/* CTA */}
                      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          data-testid={`rec-launch-${rec.segment_id}`}
                          onClick={() => !isEmpty && onLaunch && onLaunch(rec)}
                          disabled={isEmpty}
 
                          style={{
                            padding: "8px 14px", borderRadius: 8, border: "none",
                            background: isEmpty ? "#9CA3AF" : meta.color,
                            color: "#fff", fontWeight: 800,
                            cursor: isEmpty ? "not-allowed" : "pointer",
                            opacity: isEmpty ? 0.7 : 1,
                            fontSize: 12.5,
                          }}
                         aria-label={isEmpty ? "لا جمهور — الحملة معطَّلة" : ""}>
                          🚀 نفّذ هذه الحملة
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(rec.whatsapp_template);
                          }}
                          data-testid={`rec-copy-${rec.segment_id}`}
                          style={{
                            padding: "8px 14px", borderRadius: 8,
                            background: "#fff", color: "var(--brown-deep)",
                            border: "1px solid var(--border)", fontWeight: 700, cursor: "pointer", fontSize: 12.5,
                          }}
                        >
                          📋 انسخ النص
                        </button>
                      </div>

                      {/* Samples */}
                      {rec.samples?.length > 0 && (
                        <div style={{ marginTop: 12, fontSize: 11, color: "var(--muted)", lineHeight: 1.8 }}>
                          📋 أمثلة من العملاء:&nbsp;
                          {rec.samples.map((s, i) => (
                            <span key={i} style={{ marginInlineEnd: 8 }}>
                              {s.name}{s.spent > 0 ? ` (${fm(s.spent)} ر / ${s.visits})` : ""}{i < rec.samples.length - 1 ? "،" : ""}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, color = "var(--brown-deep)" }) {
  return (
    <div style={{ padding: 8, borderRadius: 6, background: "var(--beige-soft)", textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 900, color, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
